<template>
  <div>
    <remove-confirm-modal modal-id="delete-group-member-confirm"
                          :member-ids="roleGroupMember.selected"
                          @confirm="confirmDeleteGroupMembers">
    </remove-confirm-modal>
    <remove-confirm-modal modal-id="delete-person-member-confirm"
                          :member-ids="rolePersonMember.selected"
                          @confirm="confirmDeletePersonMembers">
    </remove-confirm-modal>
    <member-renew-modal modal-id="member-choose-expiration"
                        :member-ids="rolePersonMember.selected"
                        @memberExpirationSelected="onMemberExpirationSelected">
    </member-renew-modal>
    <div id="edit-members-page" class="row py-5" role="tabpanel" aria-labelledby="editMembers">
      <div class="col-12">
        <h2 class="pb-3 text-center">Modify Members</h2>
        <template v-if="managedGroup.members.length > 0">
          <div class="card mb-4" v-if="personMembers.length > 0">
            <div class="card-body">
              <pipeline-status-alert
                pipeline-response-name="updatePersonMembersResponse"
                for-type="person" for-role="member">
              </pipeline-status-alert>
              <pipeline-status-alert
                pipeline-response-name="removePersonMembersResponse"
                for-type="person" for-role="member">
              </pipeline-status-alert>
              <pipeline-status-alert
                pipeline-response-name="removeMemberExpirationResponse"
                for-type="person" for-role="member">
              </pipeline-status-alert>
              <h3 class="card-title">Modify Person Members</h3>
              <table-options-filter table-id="tbl-edit-members"
                                    :per-page="tablePerPage"
                                    @perPageChanged="onPerPageChanged"
                                    @filterChanged="onFilterChanged">
              </table-options-filter>
              <b-row class="no-margin">
                <b-col>
                  <b-form-checkbox id="chk-person-select_all" v-model="rolePersonMember.selectAll"
                                   v-on:click.native.stop="selectAllMembers"
                                   :disabled="personMembersUpdating">
                    <small>Select all Person members on this page.</small>
                  </b-form-checkbox>
                </b-col>
              </b-row>
              <b-table show-empty
                       striped
                       hover
                       stacked="sm"
                       id="tbl-edit-members"
                       :no-sort-reset="true"
                       sort-by="expirationDate"
                       :items="personMembers"
                       :fields="fields"
                       :current-page="currentPage"
                       :per-page="tablePerPage"
                       :filter="tableFilter"
                       v-model="rolePersonMember.inTable"
                       @filtered="onFiltered">
                <template slot="checkbox" slot-scope="row">
                  <div class="custom-control custom-checkbox custom-control-inline">
                    <label :for="'chk-row-'+row.item.roleId" class="sr-only">{{row.item.roleId}}</label>
                    <!--suppress HtmlFormInputWithoutLabel lint checks do not pick up the dynamic id-->
                    <input :disabled="personMembersUpdating"
                           type="checkbox"
                           :id="'chk-row-'+row.item.roleId"
                           :key="row.index"
                           :value="row.item.roleId"
                           v-model="rolePersonMember.selected"
                           :aria-label="'Select/de-select '+row.item.roleId"
                           class="custom-control-input"/>
                    <label :for="'chk-row-'+row.item.roleId"
                           :aria-label="row.item.roleId"
                           class="custom-control-label">
                    </label>
                  </div>
                </template>
                <template slot="displayName" slot-scope="row">
                  {{row.item.roleId}} ({{row.item.displayName}})
                </template>
              </b-table>
              <b-row>
                <b-col md="6" class="my-1">
                  <b-pagination id="mnu-pagination" :total-rows="totalRows" :per-page="tablePerPage"
                                v-model="currentPage" class="my-0">
                  </b-pagination>
                </b-col>
              </b-row>
              <div class="card-footer">
                <div class="d-flex flex-wrap justify-content-around">
                  <b-button v-b-modal.delete-person-member-confirm
                            id="deleteMemberBtn" class="mt-4" type="submit" variant="primary"
                            :disabled="$v.rolePersonMember.selected.$invalid || personMembersUpdating">
                    {{ rolePersonMember.removingMembers ? 'Removing...' : 'Delete Member(s)' }}
                  </b-button>
                  <b-button id="removeExpBtn" class="mt-4" type="submit" variant="primary"
                            @click="showRemoveExpModal"
                            :disabled="$v.rolePersonMember.selected.$invalid || personMembersUpdating">
                    Remove Expiration
                  </b-button>
                  <b-button v-b-modal.member-choose-expiration id="chooseExpBtn" class="mt-4" type="button"
                            variant="primary"
                            :disabled="rolePersonMember.selected.length === 0 || personMembersUpdating">
                    {{ rolePersonMember.settingExpiration ? 'Submitting...' : 'Choose Expiration' }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
          <div class="card" v-if="groupMembers.length > 0 && isAdminOrManagerOfCurrentGroup">
            <div class="card-body">
              <pipeline-status-alert
                pipeline-response-name="removeGroupMembersResponse"
                for-type="group" for-role="member">
              </pipeline-status-alert>
              <h3 class="card-title">Modify Group Members</h3>
              <label for="groupMembersList">Remove Groups as Members</label>
              <div class="row">
                <div v-for="grpMember in groupMembers" :key="grpMember.roleId" class="col-sm-6 col-lg-4 form-group">
                  <div id="groupMembersList" class="custom-control custom-checkbox">
                    <label :for="'chk-grpMember-'+grpMember.roleId" class="sr-only">{{grpMember.roleId}}</label>
                    <!--suppress HtmlFormInputWithoutLabel lint checks do not pick up the dynamic id-->
                    <input class="custom-control-input"
                           type="checkbox"
                           v-model="$v.roleGroupMember.selected.$model"
                           :value="grpMember.roleId"
                           :id="'chk-grpMember-'+grpMember.roleId"
                           :disabled="roleGroupMember.submitting">
                    <label class="custom-control-label" :for="'chk-grpMember-'+grpMember.roleId">
                      {{grpMember.roleId}}
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div class="card-footer">
              <b-btn v-b-modal.delete-group-member-confirm
                     variant="primary"
                     id="deleteGroupMembersBtn"
                     :disabled="$v.roleGroupMember.selected.$invalid || roleGroupMember.submitting">
                {{ roleGroupMember.submitting ? 'Removing...' : 'Remove Group Members'}}
              </b-btn>
            </div>
          </div>
        </template>
        <template v-else>
          <b-alert id="sta-modify-members" show>There are no members in group {{managedGroup.uugid}}.
            <b-link id="lnk-add-members" @click="$emit('changeView', 'appAddMembers' )">Add some members.</b-link>
          </b-alert>
        </template>

        <b-modal id="removeExpiration" title="Remove Expiration" ref="removeExpModalRef" @shown="resetRemoveInput">
          <b-row>
            <b-col>
              <b-form-checkbox id="removeExpirationCheckbox" v-model="removeExpirationStatus"
                               value="accepted" unchecked-value="not_accepted">
                Remove expiration from the selected members
              </b-form-checkbox>
            </b-col>
          </b-row>
          <div slot="modal-footer">
            <b-button @click="hideRemoveExpModal" variant="link">Cancel</b-button>
            <b-button id="btn-remove-exp" @click="submitRemoveExpiration" variant="primary"
                      :disabled="removeExpirationStatus === 'not_accepted' || submittingRemExp === true">
              {{submittingRemExp === true ? 'Submitting...' : 'Remove Expiration'}}
            </b-button>
          </div>
        </b-modal>
      </div>
    </div>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { eventBus } from '../main';
import { auth, groupsMixin } from '../util/groups-mixin';
import tableMixin from '../util/table-mixin';
import PipelineStatusAlert from './PipelineStatusAlert';
import RemoveConfirmModal from './modals/RemoveMemberConfirmModal';
import MemberRenewModal from './modals/MemberRenewModal';
import TableOptionsFilter from './table/TableOptionsFilter';

export default {
  name: 'EditMembers',
  title: 'Edit Members',
  mixins: [auth, groupsMixin, tableMixin],
  props: {
    managedGroup: {
      type: Object,
      required: true,
    },
    uugid: {
      type: String,
    },
  },
  data() {
    return {
      fields: [
        {
          key: 'checkbox',
          label: 'Select',
          sortable: false,
          tdClass: 'tbl-col-sm-ck',
          thClass: 'tbl-col-header-checkbox-label',
        },
        {
          key: 'displayName',
          label: 'Member',
          sortable: true,
          tdClass: 'tbl-col-sm-td',
          thClass: ['tbl-col-sm-td', 'tbl-header-member'],
        },
        {
          key: 'expirationDate',
          label: 'Expiration Date',
          sortable: true,
          formatter: 'replaceBlank',
          tdClass: 'tbl-col-sm-td',
          thClass: 'tbl-header-exp',
        },
      ],
      removeExpirationStatus: 'not_accepted',
      pipelineResponses: [
        { statusShown: false, responseName: 'updatePersonMembersResponse' },
        { statusShown: false, responseName: 'removeGroupMembersResponse' },
        { statusShown: false, responseName: 'removePersonMembersResponse' },
        { statusShown: false, responseName: 'removeMemberExpirationResponse' },
      ],
      /* Person members for current page of the table */
      submittingRemExp: false,
      windowWidth: 0,
      roleGroupMember: {
        selected: [],
        submitting: false,
      },
      rolePersonMember: {
        selected: [],
        inTable: [],
        selectAll: false,
        removingMembers: false,
        settingExpiration: false,
        removingExpiration: false,
      },
    };
  },
  computed: {
    personMembers() {
      return this.$store.getters.groupMembers(this.managedGroup, 'person');
    },
    groupMembers() {
      return this.$store.getters.groupMembers(this.managedGroup, 'group');
    },
    // eslint-disable-next-line
    selectedPersonMembers() {
      return this.rolePersonMember.selected;
    },
    personMembersUpdating() {
      return this.rolePersonMember.removingMembers || this.rolePersonMember.removingExpiration ||
        this.rolePersonMember.settingExpiration;
    },
  },
  watch: {
    selectedPersonMembers() {
      this.rolePersonMember.selectAll =
        (this.rolePersonMember.inTable.length === this.rolePersonMember.selected.length);
    },
    tablePerPage(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.clearSelectedPersonMembers();
      }
    },
    currentPage(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.clearSelectedPersonMembers();
      }
    },
    tableFilter(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.clearSelectedPersonMembers();
      }
    },
  },
  validations: {
    roleGroupMember: {
      selected: { required },
    },
    rolePersonMember: {
      selected: { required },
    },
  },
  created() {
    /* Only used for clearing person members after setting expiration. */
    eventBus.$on('clearPersonMembers', () => {
      if (this.rolePersonMember.selected.length > 0) {
        this.clearPersonMembers();
      }
    });
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
  },
  destroyed() {
    window.removeEventListener('resize', this.handleResize);
  },
  activated() {
    this.clearStatusAlerts();
    this.track();
  },
  /* Called when this dynamic component is DEACTIVATED via the DashBoard. This is always called when the component is
   * is no longer rendered. */
  deactivated() {
    this.clearStatusAlerts();
  },
  methods: {
    submitMemberExpirationRequest(date, responseToSet) {
      this.clearStatusAlerts();
      this.rolePersonMember.settingExpiration = true;
      this.$store.dispatch('batchRoleExpirationUpdate',
        {
          uugid: this.managedGroup.uugid,
          type: 'members',
          kind: 'person',
          roleIds: this.rolePersonMember.selected,
          modify: 'replace',
          expiration: date,
          responseToSet,
        })
        .finally(() => {
          this.rolePersonMember.selected = [];
          this.rolePersonMember.settingExpiration = false;
          this.scrollToElement('sta-pipe-success-person-member');
        });
    },
    /* Update Person Member(s) expiration */
    onMemberExpirationSelected(date) {
      this.submitMemberExpirationRequest(date, 'updatePersonMembersResponse');
    },
    /* Remove Person Member(s) expiration */
    submitRemoveExpiration() {
      this.hideRemoveExpModal();
      this.submitMemberExpirationRequest('', 'removeMemberExpirationResponse');
    },
    confirmDeleteGroupMembers() {
      const vm = this;
      vm.roleGroupMember.submitting = true;
      this.submitDeleteMembers('group', this.roleGroupMember.selected)
        .finally(() => {
          this.clearGroupMembers();
          vm.roleGroupMember.submitting = false;
        });
    },
    confirmDeletePersonMembers() {
      const vm = this;
      vm.rolePersonMember.removingMembers = true;
      this.submitDeleteMembers('person', this.rolePersonMember.selected)
        .finally(() => {
          vm.clearPersonMembers();
          vm.rolePersonMember.removingMembers = false;
        });
    },
    submitDeleteMembers(roleKind, membersToRemove) {
      this.clearStatusAlerts();
      const responseToSet = roleKind === 'person' ? 'removePersonMembersResponse' : 'removeGroupMembersResponse';
      this.$store.dispatch('clearPipelinedStateResponse', responseToSet);
      return this.$store.dispatch('batchModifyGroupRoles',
        {
          uugid: this.uugid,
          type: 'members',
          kind: roleKind,
          roleIds: membersToRemove,
          modify: 'remove',
          responseToSet,
        });
    },
    clearStatusAlerts() {
      const vm = this;
      /* Clear the pipelined requests state for each card on this page. */
      this.pipelineResponses.forEach((response) => {
        vm.$store.dispatch('clearPipelinedStateResponse', response.responseName);
      });
    },
    resetRemoveInput() {
      this.removeExpirationStatus = 'not_accepted';
    },
    replaceBlank(value) {
      const date = new Date(value);
      return (typeof (value) === 'undefined' || value === null) ? 'Not set' : date.toDateString();
    },
    showRemoveExpModal() {
      this.clearStatusAlerts();
      this.$refs.removeExpModalRef.show();
    },
    hideRemoveExpModal() {
      this.$refs.removeExpModalRef.hide();
      this.removeExpirationStatus = 'not_accepted';
    },
    clearPersonMembers() {
      this.rolePersonMember.selected = [];
      this.$nextTick(() => { this.$v.$reset(); });
    },
    clearGroupMembers() {
      this.roleGroupMember.selected = [];
      this.$nextTick(() => { this.$v.$reset(); });
    },
    selectAllMembers() {
      /* Don't allow execution when person members are actively being updated via http request. */
      if (!this.personMembersUpdating) {
        const vm = this;
        this.rolePersonMember.selected = [];
        if (!this.rolePersonMember.selectAll) {
          this.rolePersonMember.inTable.forEach((member) => {
            vm.rolePersonMember.selected.push(member.roleId);
          });
        }
      }
    },
    clearSelectedPersonMembers() {
      this.rolePersonMember.selectAll = false;
      this.rolePersonMember.selected = [];
    },
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
    track() {
      this.$gtag.pageview({
        page_title: 'Edit Members',
        page_path: `/group/${this.managedGroup.uugid}`,
      });
    },
  },
  components: {
    PipelineStatusAlert,
    RemoveConfirmModal,
    MemberRenewModal,
    TableOptionsFilter,
  },
};
</script>

<style>
  /*noinspection CssUnusedSymbol*/
  @media (min-width: 577px) {
    /* Hide the label on displays larger than xs breakpoint. */
    .tbl-col-header-checkbox-label {
      color: transparent !important;
      font-size: .25em;
    }
    .tbl-col-sm-ck {
      max-width: 10%;
    }
    .tbl-col-sm-td {
      max-width: 45%;
    }
    .no-margin {
      margin: 0;
    }
  }
  table label {
    margin: 0;
  }
</style>



// WEBPACK FOOTER //
// src/components/EditMembers.vue